import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';
import { Table } from '../../types/tables.types';

const RELATIONSHIP_TYPES = ['one-to-one', 'one-to-many', 'many-to-one'];

export interface RelationshipFormData {
  id?: string;
  sourceTableId: string;
  targetTableId: string;
  joinCondition: string;
  relationshipType: string;
  description: string;
  businessContext: string;
  isIdentifying: boolean;
}

interface CreateEditRelationshipModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: RelationshipFormData) => void;
  mode: 'create' | 'edit';
  saving: boolean;
  saveError: string | null;
  sourceTableId: string;
  tables: Table[];
  loadingTables: boolean;
}

const CreateEditRelationshipModal: React.FC<CreateEditRelationshipModalProps> = ({
  open,
  onClose,
  onSave,
  mode,
  saving,
  saveError,
  sourceTableId,
  tables,
  loadingTables,
}) => {
  const { selectedRelationship } = useAppSelector((state) => state.relationships);

  const [formData, setFormData] = useState({
    targetTableId: '',
    joinCondition: '',
    relationshipType: '',
    description: '',
    businessContext: '',
    isIdentifying: false,
  });

  const [selectedTargetTable, setSelectedTargetTable] = useState<Table | null>(null);

  useEffect(() => {
    if (mode === 'edit' && selectedRelationship) {
      setFormData({
        targetTableId: selectedRelationship.targetTableId || '',
        joinCondition: selectedRelationship.joinCondition || '',
        relationshipType: selectedRelationship.relationshipType || '',
        description: selectedRelationship.description || '',
        businessContext: selectedRelationship.businessContext || '',
        isIdentifying: selectedRelationship.isIdentifying || false,
      });
      const targetTable = tables.find((t) => t.id === selectedRelationship.targetTableId) || null;
      setSelectedTargetTable(targetTable);
    } else if (mode === 'create') {
      setFormData({
        targetTableId: '',
        joinCondition: '',
        relationshipType: '',
        description: '',
        businessContext: '',
        isIdentifying: false,
      });
      setSelectedTargetTable(null);
    }
  }, [mode, selectedRelationship, open, tables]);

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTargetTableChange = (_event: React.SyntheticEvent, value: Table | null) => {
    setSelectedTargetTable(value);
    setFormData((prev) => ({
      ...prev,
      targetTableId: value?.id || '',
    }));
  };

  const handleSubmit = () => {
    const data: RelationshipFormData = {
      sourceTableId,
      targetTableId: formData.targetTableId,
      joinCondition: formData.joinCondition,
      relationshipType: formData.relationshipType,
      description: formData.description,
      businessContext: formData.businessContext,
      isIdentifying: formData.isIdentifying,
    };

    if (mode === 'edit' && selectedRelationship) {
      data.id = selectedRelationship.id;
    }

    onSave(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{mode === 'create' ? 'Crear Nueva Relación' : 'Editar Relación'}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {saveError && (
            <Alert severity="error">{saveError}</Alert>
          )}

          <Autocomplete
            options={tables}
            getOptionLabel={(option) => `${option.schemaName}.${option.tableName}`}
            value={selectedTargetTable}
            onChange={handleTargetTableChange}
            loading={loadingTables}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tabla Destino"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingTables ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Autocomplete
            options={RELATIONSHIP_TYPES}
            value={formData.relationshipType || null}
            onChange={(_event, value) => handleChange('relationshipType', value || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tipo de Relación"
                required
              />
            )}
          />

          <TextField
            label="Condición de Join"
            value={formData.joinCondition}
            onChange={(e) => handleChange('joinCondition', e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
          />

          <TextField
            label="Descripción"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            fullWidth
            multiline
            rows={3}
          />

          <TextField
            label="Contexto de Negocio"
            value={formData.businessContext}
            onChange={(e) => handleChange('businessContext', e.target.value)}
            fullWidth
            multiline
            rows={3}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isIdentifying}
                onChange={(e) => handleChange('isIdentifying', e.target.checked)}
              />
            }
            label="Es Identificante"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !formData.targetTableId || !formData.relationshipType}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Guardando...' : mode === 'create' ? 'Crear' : 'Actualizar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditRelationshipModal;
